import { useTranslation } from 'react-i18next';
import { Control, UseFormWatch, UseFormSetValue, Controller, useFormState } from 'react-hook-form';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ContactSelector } from '@/widgets/contact-selector';
import { ConsignmentSummary } from '@/entities/consignments';

import { ConsignmentFormValues } from '../model/validation';

interface PointsOfContactCardProps {
  control: Control<ConsignmentFormValues>;
  watch: UseFormWatch<ConsignmentFormValues>;
  setValue: UseFormSetValue<ConsignmentFormValues>;
  consignmentData?: ConsignmentSummary;
  isReadOnly?: boolean;
}

export const PointsOfContactCard = ({
  control,
  watch,
  setValue,
  consignmentData,
  isReadOnly = false
}: PointsOfContactCardProps) => {
  const { t } = useTranslation();
  const { errors } = useFormState({ control });

  return (
    <Card className="md:col-span-3">
      <CardHeader className="border-b bg-slate-50/50 py-3">
        <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
          {t('consignment.sections.pointOfContact', 'Points of Contact')}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          <Controller
            name="senderContact"
            control={control}
            render={({ field }) => (
              <ContactSelector
                label={t('consignment.labels.sender', 'Sender')}
                organizationId={watch('senderOrganization')}
                organizationName={consignmentData?.senderContact?.organization?.name}
                contactId={field.value}
                onOrganizationChange={(id) => {
                  setValue('senderOrganization', id ?? '', { shouldValidate: true });
                  setValue('senderContact', '');
                }}
                onContactChange={field.onChange}
                disabled={isReadOnly}
                required
                organizationError={errors.senderOrganization?.message}
                contactError={errors.senderContact?.message}
              />
            )}
          />

          <Controller
            name="receiverContact"
            control={control}
            render={({ field }) => (
              <ContactSelector
                label={t('consignment.labels.receiver', 'Receiver')}
                organizationId={watch('receiverOrganization')}
                organizationName={consignmentData?.receiverContact?.organization?.name}
                contactId={field.value}
                onOrganizationChange={(id) => {
                  setValue('receiverOrganization', id ?? '', { shouldValidate: true });
                  setValue('receiverContact', '');
                }}
                onContactChange={field.onChange}
                disabled={isReadOnly}
                required
                organizationError={errors.receiverOrganization?.message}
                contactError={errors.receiverContact?.message}
              />
            )}
          />

          <Controller
            name="clearingAgentContact"
            control={control}
            render={({ field }) => (
              <ContactSelector
                label={t('consignment.labels.clearingAgent', 'Clearing Agent')}
                organizationId={watch('clearingAgent')}
                organizationName={consignmentData?.clearingAgent?.organization?.name}
                contactId={field.value}
                onOrganizationChange={(id) => {
                  setValue('clearingAgent', id);
                  setValue('clearingAgentContact', '');
                }}
                onContactChange={field.onChange}
                disabled={isReadOnly}
              />
            )}
          />
        </div>
      </CardContent>
    </Card>
  );
};
